import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import PhoneInput from '@/components/PhoneInput';
import { toast } from 'sonner';
import { Plus, Trash2, Search, X } from 'lucide-react';

export default function MeasurementsTab() {
  const [mode, setMode] = useState('idle'); // 'idle' | 'add' | 'search'
  const [categories, setCategories] = useState([]);

  // Search state
  const [searchPhone, setSearchPhone] = useState(''); // stored as +91XXXXXXXXXX
  const [searchResults, setSearchResults] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [searching, setSearching] = useState(false);

  // Add state
  const [form, setForm] = useState({user_id:'', name:'', category_slug:'', measurements:{}});
  const [selectedCat, setSelectedCat] = useState(null);
  const [addPhone, setAddPhone] = useState('');
  const [addSearchResults, setAddSearchResults] = useState([]);
  const [addSelectedUser, setAddSelectedUser] = useState(null);

  useEffect(() => {
    api.get('/categories').then(r => setCategories(r.data.data || [])).catch(()=>{});
  }, []);

  // Live search for "Search Measurements"
  useEffect(() => {
    const digits = searchPhone.replace(/^\+91/, '');
    if (digits.length >= 3 && !selectedUser) {
      api.get(`/users/search?phone=${encodeURIComponent(digits)}`)
        .then(r => setSearchResults(r.data.data || []))
        .catch(()=>{});
    } else {
      setSearchResults([]);
    }
  }, [searchPhone, selectedUser]);

  // Live search for "Add Measurement" user picker
  useEffect(() => {
    const digits = addPhone.replace(/^\+91/, '');
    if (digits.length >= 3 && !addSelectedUser) {
      api.get(`/users/search?phone=${encodeURIComponent(digits)}`)
        .then(r => setAddSearchResults(r.data.data || []))
        .catch(()=>{});
    } else {
      setAddSearchResults([]);
    }
  }, [addPhone, addSelectedUser]);

  const pickSearchUser = async (u) => {
    setSelectedUser(u);
    setSearchPhone(u.phone || '');
    setSearchResults([]);
    setSearching(true);
    try {
      const r = await api.get(`/admin/measurements?user_id=${u.id}`);
      setProfiles(r.data.data || []);
    } catch { toast.error('Failed to load measurements'); }
    finally { setSearching(false); }
  };

  const clearSearch = () => {
    setSearchPhone('');
    setSelectedUser(null);
    setSearchResults([]);
    setProfiles([]);
  };

  const resetAdd = () => {
    setForm({user_id:'', name:'', category_slug:'', measurements:{}});
    setSelectedCat(null);
    setAddSelectedUser(null);
    setAddPhone('');
    setAddSearchResults([]);
  };

  const handleCreate = async () => {
    if (!form.user_id || !form.name || !form.category_slug) {
      toast.error('User, name and category are required');
      return;
    }
    try {
      await api.post(`/admin/measurements/user/${form.user_id}`, {
        name: form.name,
        category_slug: form.category_slug,
        measurements: form.measurements,
      });
      toast.success('Measurement profile created');
      resetAdd();
      setMode('idle');
    } catch(e) { toast.error(e.response?.data?.error || 'Failed'); }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/admin/measurements/${id}`);
      toast.success('Deleted');
      // Refresh list for the currently selected user
      if (selectedUser) {
        const r = await api.get(`/admin/measurements?user_id=${selectedUser.id}`);
        setProfiles(r.data.data || []);
      }
    } catch { toast.error('Failed'); }
  };

  return (
    <div data-testid="admin-measurements">
      {mode === 'idle' && (
        <div className="flex flex-wrap gap-2 mb-4">
          <Button variant="outline" onClick={() => setMode('add')} className="rounded-full" data-testid="add-measurement-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Measurement
          </Button>
          <Button variant="outline" onClick={() => setMode('search')} className="rounded-full" data-testid="search-measurement-btn">
            <Search className="w-4 h-4 mr-2" /> Search Measurements
          </Button>
        </div>
      )}

      {mode === 'search' && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <p className="text-sm font-semibold">Search Customer's Measurements</p>
            <Button variant="ghost" size="sm" onClick={() => { clearSearch(); setMode('idle'); }} className="ml-auto text-xs h-7">Close</Button>
          </div>
          <div className="relative max-w-sm">
            <PhoneInput
              value={searchPhone}
              onChange={(v) => { setSearchPhone(v); setSelectedUser(null); setProfiles([]); }}
              placeholder="Search customer by phone"
              data-testid="measurement-search-phone"
            />
            {searchResults.length > 0 && !selectedUser && (
              <div className="absolute z-10 top-full left-0 right-0 bg-white border rounded-lg shadow-lg mt-1 max-h-48 overflow-y-auto">
                {searchResults.map(u => (
                  <button
                    key={u.id}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-muted"
                    onClick={() => pickSearchUser(u)}
                    data-testid={`search-pick-${u.id}`}
                  >
                    {u.name || 'Unnamed'} <span className="text-muted-foreground text-xs">({u.phone})</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {selectedUser && (
            <div className="border rounded-xl p-3 bg-muted/20 flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold">{selectedUser.name || 'Unnamed'}</p>
                <p className="text-xs text-muted-foreground">{selectedUser.phone}</p>
              </div>
              <button onClick={clearSearch} className="text-muted-foreground hover:text-foreground" data-testid="clear-search-btn"><X className="w-4 h-4" /></button>
            </div>
          )}

          {selectedUser && (
            <>
              {searching && <p className="text-sm text-muted-foreground">Loading...</p>}
              {!searching && profiles.length === 0 && <p className="text-sm text-muted-foreground">No measurement profiles found for this customer.</p>}
              {profiles.map(m => (
                <div key={m.id} className="border rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="text-sm font-medium">{m.name}</p>
                      <p className="text-[10px] text-primary uppercase font-medium">{m.category_slug}</p>
                    </div>
                    <button onClick={() => handleDelete(m.id)} className="text-muted-foreground hover:text-destructive" data-testid={`delete-measurement-${m.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-1 text-[11px]">
                    {Object.entries(m.measurements || {})
                      .filter(([,v]) => v !== '' && v !== null)
                      .map(([k,v]) => (
                        <div key={k}><span className="text-muted-foreground capitalize">{k.replace(/_/g,' ')}:</span> <span className="font-medium">{v}</span></div>
                      ))}
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      )}

      {mode === 'add' && (
        <div className="border rounded-xl p-4 space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold">Add Measurement Profile</p>
            <Button variant="ghost" size="sm" onClick={() => { resetAdd(); setMode('idle'); }} className="text-xs h-7">Close</Button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="relative">
              <PhoneInput
                value={addPhone}
                onChange={(v) => { setAddPhone(v); setAddSelectedUser(null); setForm(f => ({...f, user_id:''})); }}
                placeholder="Customer phone"
                data-testid="measurement-phone-search"
              />
              {addSearchResults.length > 0 && !addSelectedUser && (
                <div className="absolute z-10 top-full left-0 right-0 bg-white border rounded-lg shadow-lg mt-1 max-h-48 overflow-y-auto">
                  {addSearchResults.map(u => (
                    <button
                      key={u.id}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-muted"
                      onClick={() => { setAddSelectedUser(u); setForm(f => ({...f, user_id: u.id})); setAddSearchResults([]); setAddPhone(u.phone || ''); }}
                      data-testid={`search-result-${u.id}`}
                    >
                      {u.name || 'Unnamed'} <span className="text-muted-foreground text-xs">({u.phone})</span>
                    </button>
                  ))}
                </div>
              )}
              {addSelectedUser && <p className="text-xs text-primary mt-1">Selected: {addSelectedUser.name || 'Unnamed'} ({addSelectedUser.phone})</p>}
            </div>
            <select
              value={form.category_slug}
              onChange={e => { const c = categories.find(x => x.slug === e.target.value); setSelectedCat(c); setForm(f => ({...f, category_slug: e.target.value, measurements: {}})); }}
              className="rounded-lg border px-3 py-2 text-sm"
              data-testid="measurement-category-select"
            >
              <option value="">Select Category</option>
              {categories.map(c => <option key={c.slug} value={c.slug}>{c.name}</option>)}
            </select>
          </div>
          <Input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} placeholder="Profile Name (e.g. Mom's Sangeet Blouse)" data-testid="measurement-name" />
          {selectedCat?.measurement_fields?.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {selectedCat.measurement_fields.map(f => (
                <div key={f.field_key} className="flex items-center gap-2">
                  <label className="text-xs w-36 flex-shrink-0">
                    {f.field_label}{f.is_required && <span className="text-destructive">*</span>}
                  </label>
                  <Input
                    type={f.field_type === 'fraction' ? 'text' : 'number'}
                    step="0.25"
                    value={form.measurements[f.field_key] ?? ''}
                    onChange={e => setForm(prev => ({
                      ...prev,
                      measurements: { ...prev.measurements, [f.field_key]: f.field_type === 'fraction' ? e.target.value : (e.target.value === '' ? '' : parseFloat(e.target.value)) },
                    }))}
                    placeholder={f.field_type === 'fraction' ? '12.5/14' : '0'}
                    className="max-w-[100px]"
                  />
                </div>
              ))}
            </div>
          )}
          <div className="flex gap-2">
            <Button onClick={handleCreate} className="rounded-full" data-testid="measurement-save-btn">Save</Button>
            <Button variant="outline" onClick={() => { resetAdd(); setMode('idle'); }} className="rounded-full">Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
}
